import { useEffect, useRef, useState } from 'react'
import { Loader2, Trash2 } from 'lucide-react'
import { Button } from '@/components/shadcn/button'
import { Checkbox } from '@/components/shadcn/checkbox'
import { Progress } from '@/components/shadcn/progress'
import { FilesClient, joinPath, type FileEntry } from '@/lib/files'
import { cn } from '@/lib/cn'
import { formatBytes } from './AttachmentTray'

/** Past this the session's uploads are worth clearing out: they live on the server's disk. */
export const UPLOADS_WARN_BYTES = 1024 ** 3

/**
 * The files sent into this session's `uploads/` folder: how much room they
 * take, and a list to tick and delete. Opens its own files socket only while
 * shown.
 */
export function UploadsList({ sessionId }: { sessionId: string }) {
  const client = useRef<FilesClient | null>(null)
  const [dir, setDir] = useState<string | null>(null)
  const [files, setFiles] = useState<FileEntry[] | null>(null)
  const [writable, setWritable] = useState(false)
  const [picked, setPicked] = useState<Set<string>>(new Set())
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    let uploads = ''
    const c = new FilesClient(sessionId, 'rw', {
      ready: (r) => { uploads = r.uploads; setDir(r.uploads); setWritable(r.writable); c.watch(r.uploads) },
      // No folder yet means nothing was ever uploaded.
      listing: (path, entries) => { if (path === uploads) setFiles((entries ?? []).filter((e) => !e.dir).sort((a, b) => b.mtime_ms - a.mtime_ms)) },
      state: (s) => { if (s === 'unreachable' || s === 'ended') setErr('Cannot reach the server') },
    })
    client.current = c
    return () => { c.stop(); client.current = null }
  }, [sessionId])

  // A file deleted elsewhere is no longer picked.
  useEffect(() => { if (files) setPicked((p) => new Set([...p].filter((n) => files.some((f) => f.name === n)))) }, [files])

  if (err && !files) return <div className="p-3 text-xs text-err">{err}</div>
  if (!files || !dir) return <div className="flex items-center gap-2 p-3 text-xs text-muted-foreground"><Loader2 className="size-3.5 animate-spin" />Loading…</div>

  const total = files.reduce((n, f) => n + f.bytes, 0)
  const over = total > UPLOADS_WARN_BYTES
  const all = files.length > 0 && picked.size === files.length
  const del = async (names: string[]) => {
    setBusy(true); setErr(null)
    try {
      for (const n of names) await client.current?.remove(joinPath(dir, n))
      setPicked(new Set())
    } catch (e) {
      setErr((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex flex-col">
      <div className="flex flex-col gap-1.5 px-3 py-2.5">
        <div className="flex items-baseline justify-between text-xs">
          <span className={cn('font-medium tabular-nums', over && 'text-warn')}>{formatBytes(total)} used</span>
          <span className="text-muted-foreground">{files.length} file{files.length === 1 ? '' : 's'} · of 1 GB</span>
        </div>
        <Progress value={Math.min(100, (total / UPLOADS_WARN_BYTES) * 100)} className={cn('h-1', over && '[&>div]:bg-warn')} />
        {over && <p className="text-xs text-warn">Over 1 GB. Delete the files Claude no longer needs.</p>}
      </div>
      {files.length > 0 && (
        <div className="flex h-8 items-center gap-2 border-y px-3 text-xs">
          <Checkbox checked={all ? true : picked.size ? 'indeterminate' : false} disabled={!writable} aria-label="Select all"
            onCheckedChange={(v) => setPicked(v === true ? new Set(files.map((f) => f.name)) : new Set())} />
          <span className="text-muted-foreground">{picked.size ? `${picked.size} selected` : 'Select all'}</span>
          {writable && picked.size > 0 && (
            <Button variant="ghost" size="xs" className="ml-auto gap-1 text-err hover:text-err" disabled={busy} onClick={() => void del([...picked])}>
              {busy ? <Loader2 className="size-3 animate-spin" /> : <Trash2 className="size-3" />}Delete{all ? ' all' : ''}
            </Button>
          )}
        </div>
      )}
      {err && <div className="px-3 pt-2 text-xs text-err">{err}</div>}
      {files.length === 0 ? <div className="px-3 py-6 text-center text-xs text-muted-foreground">No uploaded files</div> : (
        <ul className="max-h-64 overflow-y-auto py-1">
          {files.map((f) => (
            <li key={f.name}>
              <label className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-xs hover:bg-hover">
                <Checkbox checked={picked.has(f.name)} disabled={!writable}
                  onCheckedChange={(v) => setPicked((p) => { const n = new Set(p); if (v === true) n.add(f.name); else n.delete(f.name); return n })} />
                <span className="min-w-0 flex-1 truncate" title={f.name}>{f.name}</span>
                <span className="shrink-0 tabular-nums text-muted-foreground">{formatBytes(f.bytes)}</span>
              </label>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// One live listing of a session's uploads/ shared by every sent-file square,
// so a deleted file shows as deleted without each square asking the box.
type Names = { names: Set<string> | null; subs: Set<() => void>; client: FilesClient }
const listings = new Map<string, Names>()

function listing(sessionId: string): Names {
  let l = listings.get(sessionId)
  if (l) return l
  let uploads = ''
  const entry: Names = {
    names: null, subs: new Set(),
    client: new FilesClient(sessionId, 'ro', {
      ready: (r) => { uploads = r.uploads; entry.client.watch(r.uploads) },
      listing: (path, entries) => {
        if (path !== uploads) return
        entry.names = new Set((entries ?? []).filter((e) => !e.dir).map((e) => e.name))
        for (const f of entry.subs) f()
      },
      state: () => {},
    }),
  }
  listings.set(sessionId, entry)
  l = entry
  return l
}

/** The names in the session's uploads/ now; null until the box has answered. */
export function useUploadNames(sessionId: string | undefined): Set<string> | null {
  const [, bump] = useState(0)
  useEffect(() => {
    if (!sessionId) return
    const l = listing(sessionId)
    const f = () => bump((n) => n + 1)
    l.subs.add(f)
    f()
    return () => {
      l.subs.delete(f)
      // The last square gone (session closed): drop the socket.
      if (!l.subs.size) { l.client.stop(); listings.delete(sessionId) }
    }
  }, [sessionId])
  return sessionId ? listings.get(sessionId)?.names ?? null : null
}
